import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { JSONContent } from '@tiptap/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  CategorizationBlock,
  CategorizationCategory,
  CategorizationIcon,
  CategorizationItem,
  CategorizationTitleLevel,
} from '@course-authoring/shared-types';
import {
  richTextToPlainText,
  useTheme,
} from '@course-authoring/shared-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import { InteractionRichTextField } from './InteractionRichTextField';
import {
  createTokenRichTextToolbarStyles,
  EMPTY_RICH_TEXT_DOC,
  reorderById,
} from './interactionEditorUtils';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';
import { ImageInsertModal } from './ImageInsertModal';
import { QuestionSelector } from './questionBank/QuestionSelector';
import {
  QuestionSectionLabel,
  createReadOnlyTextStyle,
  resolveQuestionEditorMode,
} from './questionBank/questionEditorShared';
import { FieldOverrideStatus } from './questionBank/FieldOverrideStatus';
import { useFieldOverrideState } from './questionBank/useFieldOverrideState';
import { useQuestionTextOverride } from './questionBank/useQuestionTextOverride';
import EmojiPicker, { type EmojiClickData, Theme as EmojiTheme } from 'emoji-picker-react';

export interface CategorizationEditorProps {
  block: CategorizationBlock;
  onChange: (updates: Partial<CategorizationBlock>) => void;
  autoFocus?: boolean;
  /**
   * 'block' (default): lesson-block mode — title controls + QuestionSelector,
   * question content (instructions, categories, items) rendered read-only from
   * the resolved block fields. The four feedback fields stay block-editable.
   * 'questionModal': create-question modal mode — editable question fields,
   * no title controls, no lock toggles.
   */
  variant?: 'block' | 'questionModal';
  translationMode?: boolean;
  /** NA-198: when true (question in use in a published/archived lesson), disables removing persisted categories/items without hiding the controls. */
  disableOptionRemoval?: boolean;
  /** Ids of categories/items that are actually persisted — only these are locked; ones added in this draft (no server id yet) stay removable. */
  lockedCategoryIds?: ReadonlySet<string>;
  lockedItemIds?: ReadonlySet<string>;
}

type FieldName =
  | 'title'
  | 'description'
  | 'correctFeedbackHeading'
  | 'correctFeedbackBody'
  | 'incorrectFeedbackHeading'
  | 'incorrectFeedbackBody';

const editorInnerStyles = `
  .categorization-editor p {
    margin: 0;
  }
  .categorization-editor p:not(:last-child) {
    margin-bottom: 0.5em;
  }
`;

// --- Icon picker ---

interface IconPickerProps {
  icon?: CategorizationIcon;
  onChange: (icon: CategorizationIcon | undefined) => void;
  tokens: ReturnType<typeof useTheme>['tokens'];
  imageModalOpen: boolean;
  onOpenImageModal: () => void;
  onCloseImageModal: () => void;
}

function IconPicker({
  icon,
  onChange,
  tokens,
  imageModalOpen,
  onOpenImageModal,
  onCloseImageModal,
}: IconPickerProps) {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const handleEmojiClick = useCallback(
    (emojiData: EmojiClickData) => {
      onChange({ type: 'emoji', value: emojiData.emoji });
      setShowPicker(false);
    },
    [onChange]
  );

  const buttonStyle: CSSProperties = {
    width: '36px',
    height: '36px',
    border: `1px solid ${tokens.borders.default}`,
    borderRadius: tokens.radius.sm,
    backgroundColor: tokens.backgrounds.app,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: 0,
    padding: 0,
    overflow: 'hidden',
  };

  const dropdownStyle: CSSProperties = {
    position: 'absolute',
    top: '40px',
    left: 0,
    zIndex: 20,
    backgroundColor: tokens.backgrounds.app,
    border: `1px solid ${tokens.borders.default}`,
    borderRadius: tokens.radius.md,
    boxShadow: tokens.shadow.md,
    padding: tokens.spacing.sm,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.sm,
  };

  const actionButtonStyle: CSSProperties = {
    padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
    border: `1px solid ${tokens.borders.default}`,
    borderRadius: tokens.radius.sm,
    backgroundColor: tokens.backgrounds.app,
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: tokens.typography.fontFamily,
    textAlign: 'left' as const,
    width: '100%',
  };

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        style={buttonStyle}
        onClick={() => setShowPicker(!showPicker)}
        title={icon ? 'Change icon' : 'Add icon'}
      >
        {icon ? (
          icon.type === 'emoji' ? (
            icon.value
          ) : (
            <img src={icon.value} alt="" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
          )
        ) : (
          <span style={{ color: tokens.text.muted, fontSize: '14px' }}>+</span>
        )}
      </button>

      {showPicker && (
        <div ref={pickerRef} style={dropdownStyle}>
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            theme={EmojiTheme.LIGHT}
            width={350}
            height={400}
            searchPlaceHolder="Search emoji..."
            previewConfig={{ showPreview: false }}
          />
          <button
            type="button"
            onClick={() => {
              setShowPicker(false);
              onOpenImageModal();
            }}
            style={actionButtonStyle}
          >
            Upload image instead...
          </button>
          {icon && (
            <button
              type="button"
              onClick={() => {
                onChange(undefined);
                setShowPicker(false);
              }}
              style={{
                ...actionButtonStyle,
                border: 'none',
                color: '#b91c1c',
                padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
              }}
            >
              Remove icon
            </button>
          )}
        </div>
      )}

      <ImageInsertModal
        isOpen={imageModalOpen}
        onClose={onCloseImageModal}
        onInsert={(data) => {
          onChange({ type: 'image', value: data.src, imageLibraryItemId: data.libraryItemId });
          onCloseImageModal();
        }}
        targetAspectRatio="1:1"
      />
    </div>
  );
}

// --- Read-only icon display (bank-managed blocks) ---

interface ReadOnlyIconBoxProps {
  icon?: CategorizationIcon;
  tokens: ReturnType<typeof useTheme>['tokens'];
}

function ReadOnlyIconBox({ icon, tokens }: ReadOnlyIconBoxProps): React.ReactElement {
  const boxStyle: CSSProperties = {
    width: '36px',
    height: '36px',
    border: `1px solid ${tokens.borders.default}`,
    borderRadius: tokens.radius.sm,
    backgroundColor: tokens.backgrounds.app,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: 0,
    overflow: 'hidden',
  };

  return (
    <div style={boxStyle} aria-hidden="true">
      {icon ? (
        icon.type === 'emoji' ? (
          icon.value
        ) : (
          <img src={icon.value} alt="" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
        )
      ) : null}
    </div>
  );
}

// --- Sortable category row ---

function DragHandleIcon(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="3" r="1.5" />
      <circle cx="11" cy="3" r="1.5" />
      <circle cx="5" cy="8" r="1.5" />
      <circle cx="11" cy="8" r="1.5" />
      <circle cx="5" cy="13" r="1.5" />
      <circle cx="11" cy="13" r="1.5" />
    </svg>
  );
}

interface SortableCategoryRowProps {
  category: CategorizationCategory;
  categoryCount: number;
  onLabelChange: (id: string, label: string) => void;
  onIconChange: (id: string, icon: CategorizationIcon | undefined) => void;
  onRemove: (id: string) => void;
  styles: Record<string, CSSProperties>;
  tokens: ReturnType<typeof useTheme>['tokens'];
  disableOptionRemoval?: boolean;
}

function SortableCategoryRow({
  category,
  categoryCount,
  onLabelChange,
  onIconChange,
  onRemove,
  styles,
  tokens,
  disableOptionRemoval = false,
}: SortableCategoryRowProps) {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style: CSSProperties = {
    ...styles.categoryRow,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <button
        type="button"
        style={styles.dragHandle}
        aria-label="Drag to reorder"
        {...listeners}
        {...attributes}
      >
        <DragHandleIcon />
      </button>
      <IconPicker
        icon={category.icon}
        onChange={(icon) => onIconChange(category.id, icon)}
        tokens={tokens}
        imageModalOpen={imageModalOpen}
        onOpenImageModal={() => setImageModalOpen(true)}
        onCloseImageModal={() => setImageModalOpen(false)}
      />
      <input
        type="text"
        value={category.label}
        placeholder="Category label"
        onChange={(e) => onLabelChange(category.id, e.target.value)}
        style={styles.categoryInput}
      />
      {categoryCount > 2 && (
        <button
          type="button"
          style={{ ...styles.removeButton, ...(disableOptionRemoval ? styles.removeButtonDisabled : {}) }}
          onClick={() => onRemove(category.id)}
          disabled={disableOptionRemoval}
          title={disableOptionRemoval ? "Can't remove categories from a question used in a published lesson" : 'Remove category'}
          onMouseEnter={(e) => {
            if (!disableOptionRemoval) e.currentTarget.style.color = '#b91c1c';
          }}
          onMouseLeave={(e) => {
            if (!disableOptionRemoval) e.currentTarget.style.color = tokens.text.muted;
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

// --- Item row with icon ---

interface ItemRowProps {
  item: CategorizationItem;
  itemCount: number;
  availableCategories: CategorizationCategory[];
  onLabelChange: (id: string, label: string) => void;
  onIconChange: (id: string, icon: CategorizationIcon | undefined) => void;
  onCategoryChange: (id: string, categoryId: string) => void;
  onRemove: (id: string) => void;
  styles: Record<string, CSSProperties>;
  tokens: ReturnType<typeof useTheme>['tokens'];
  disableOptionRemoval?: boolean;
}

function ItemRow({
  item,
  itemCount,
  availableCategories,
  onLabelChange,
  onIconChange,
  onCategoryChange,
  onRemove,
  styles,
  tokens,
  disableOptionRemoval = false,
}: ItemRowProps) {
  const [imageModalOpen, setImageModalOpen] = useState(false);

  return (
    <div style={styles.itemRow}>
      <IconPicker
        icon={item.icon}
        onChange={(icon) => onIconChange(item.id, icon)}
        tokens={tokens}
        imageModalOpen={imageModalOpen}
        onOpenImageModal={() => setImageModalOpen(true)}
        onCloseImageModal={() => setImageModalOpen(false)}
      />
      <input
        type="text"
        value={item.label}
        placeholder="Item label"
        onChange={(e) => onLabelChange(item.id, e.target.value)}
        style={styles.itemLabelInput}
      />
      <select
        value={item.correctCategoryId}
        onChange={(e) => onCategoryChange(item.id, e.target.value)}
        style={{
          ...styles.itemCategorySelect,
          color: item.correctCategoryId ? tokens.text.primary : tokens.text.muted,
        }}
        disabled={availableCategories.length === 0}
      >
        <option value="">Select category</option>
        {availableCategories.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {cat.label}
          </option>
        ))}
      </select>
      {itemCount > 1 && (
        <button
          type="button"
          style={{ ...styles.removeButton, ...(disableOptionRemoval ? styles.removeButtonDisabled : {}) }}
          onClick={() => onRemove(item.id)}
          disabled={disableOptionRemoval}
          title={disableOptionRemoval ? "Can't remove items from a question used in a published lesson" : 'Remove item'}
          onMouseEnter={(e) => {
            if (!disableOptionRemoval) e.currentTarget.style.color = '#b91c1c';
          }}
          onMouseLeave={(e) => {
            if (!disableOptionRemoval) e.currentTarget.style.color = tokens.text.muted;
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

// --- Main editor ---

export function CategorizationEditor({
  block,
  onChange,
  autoFocus = false,
  variant = 'block',
  translationMode = false,
  disableOptionRemoval = false,
  lockedCategoryIds,
  lockedItemIds,
}: CategorizationEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedField, setFocusedField] = useState<FieldName | null>(null);

  const { isModal, isManaged, showBlockAuthoringControls } = resolveQuestionEditorMode(
    variant,
    block,
    translationMode,
  );

  const titleLevel: CategorizationTitleLevel = block.titleLevel ?? 'h2';

  // Still needed for the read-only translation-mode display below.
  const descriptionText = richTextToPlainText(block.description);

  // NA-382: question text is authored on the block (or on the bank value in the
  // modal); see useQuestionTextOverride for why the commit is on blur.
  const questionText = useQuestionTextOverride(block, onChange, isModal, translationMode);

  // NA-384: title, same override model, plain string instead of rich text.
  // See the WCS editor's identical comment for the full reasoning — ownership
  // is only claimed while the bank actually has a title, checked via
  // `titleBankValue` rather than `questionDefinitionId` since the former is
  // reliable immediately after a fresh selection.
  const titleOverride = useFieldOverrideState<CategorizationBlock, string>(block, {
    value: 'title',
    marker: 'titleOverridden',
    bankValue: 'titleBankValue',
    requireBankValue: true,
  });
  const hasBankTitle = block.titleBankValue !== undefined;

  const handleTitleChange = useCallback(
    (next: string) => onChange(
      hasBankTitle && !translationMode ? titleOverride.buildEdit(next) : { title: next },
    ),
    [hasBankTitle, translationMode, titleOverride, onChange],
  );

  const handleTitleRevert = useCallback(
    () => onChange(titleOverride.buildRevert()),
    [titleOverride, onChange],
  );

  const richTextToolbarStyles = useMemo(
    () => createTokenRichTextToolbarStyles(tokens),
    [tokens]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleFieldFocus = useCallback((field: FieldName) => {
    setFocusedField(field);
  }, []);

  const handleFieldBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  // Category handlers
  const handleCategoryLabelChange = useCallback(
    (id: string, label: string) => {
      onChange({
        categories: block.categories.map((c) =>
          c.id === id ? { ...c, label } : c
        ),
      });
    },
    [block.categories, onChange]
  );

  const handleCategoryIconChange = useCallback(
    (id: string, icon: CategorizationIcon | undefined) => {
      onChange({
        categories: block.categories.map((c) =>
          c.id === id ? { ...c, icon } : c
        ),
      });
    },
    [block.categories, onChange]
  );

  const handleCategoryRemove = useCallback(
    (id: string) => {
      onChange({
        categories: block.categories.filter((c) => c.id !== id),
        items: block.items.map((item) =>
          item.correctCategoryId === id
            ? { ...item, correctCategoryId: '' }
            : item
        ),
      });
    },
    [block.categories, block.items, onChange]
  );

  const handleCategoryAdd = useCallback(() => {
    if (block.categories.length >= 3) return;
    onChange({
      categories: [
        ...block.categories,
        { id: crypto.randomUUID(), label: '' },
      ],
    });
  }, [block.categories, onChange]);

  const handleCategoryDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        onChange({ categories: reorderById(block.categories, active.id, over.id) });
      }
    },
    [block.categories, onChange]
  );

  // Item handlers
  const handleItemLabelChange = useCallback(
    (id: string, label: string) => {
      onChange({
        items: block.items.map((item) =>
          item.id === id ? { ...item, label } : item
        ),
      });
    },
    [block.items, onChange]
  );

  const handleItemIconChange = useCallback(
    (id: string, icon: CategorizationIcon | undefined) => {
      onChange({
        items: block.items.map((item) =>
          item.id === id ? { ...item, icon } : item
        ),
      });
    },
    [block.items, onChange]
  );

  const handleItemCategoryChange = useCallback(
    (id: string, correctCategoryId: string) => {
      onChange({
        items: block.items.map((item) =>
          item.id === id ? { ...item, correctCategoryId } : item
        ),
      });
    },
    [block.items, onChange]
  );

  const handleItemRemove = useCallback(
    (id: string) => {
      onChange({ items: block.items.filter((item) => item.id !== id) });
    },
    [block.items, onChange]
  );

  const handleItemAdd = useCallback(() => {
    onChange({
      items: [
        ...block.items,
        { id: crypto.randomUUID(), label: '', correctCategoryId: '' },
      ],
    });
  }, [block.items, onChange]);

  // Categories with non-empty labels for the dropdown
  const availableCategories = useMemo(
    () => block.categories.filter((c) => c.label.trim() !== ''),
    [block.categories]
  );

  const styles = useMemo((): Record<string, CSSProperties> => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xl,
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    sectionLabelRow: {
      display: 'flex',
      alignItems: 'baseline',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.sm,
    },
    sectionLabel: {
      fontSize: '12px',
      fontWeight: 700,
      color: tokens.text.muted,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
    },
    titleInput: getTitleInputStyle(titleLevel, tokens),
    readOnlyText: createReadOnlyTextStyle(tokens),
    descriptionEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.muted,
      lineHeight: tokens.typography.body.lineHeight,
    },
    sectionCard: {
      backgroundColor: tokens.backgrounds.app,
      borderRadius: tokens.radius.md,
      padding: tokens.spacing.lg,
      border: `1px solid ${tokens.borders.default}`,
      boxSizing: 'border-box' as const,
    },
    feedbackFieldsColumn: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: tokens.spacing.lg,
    },
    categoryRow: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.sm,
    },
    dragHandle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: tokens.spacing.xs,
      cursor: 'grab',
      color: tokens.text.muted,
      background: 'none',
      border: 'none',
      borderRadius: tokens.radius.sm,
      flexShrink: 0,
    },
    categoryInput: {
      flex: 1,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      outline: 'none',
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box' as const,
    },
    removeButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: tokens.text.muted,
      fontSize: '18px',
      borderRadius: tokens.radius.sm,
      flexShrink: 0,
    },
    removeButtonDisabled: {
      cursor: 'not-allowed',
      opacity: 0.4,
    },
    addLink: {
      background: 'none',
      border: 'none',
      color: tokens.brand.primary,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      fontWeight: 500,
      padding: 0,
    },
    itemsHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.md,
      marginBottom: tokens.spacing.sm,
    },
    itemsHeaderLabel: {
      flex: 1,
      fontSize: '11px',
      fontWeight: 600,
      color: tokens.text.muted,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
    },
    itemsHeaderCategoryLabel: {
      width: '180px',
      fontSize: '11px',
      fontWeight: 600,
      color: tokens.text.muted,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
      flexShrink: 0,
    },
    itemRow: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.sm,
    },
    itemLabelInput: {
      flex: 1,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      outline: 'none',
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box' as const,
    },
    itemCategorySelect: {
      width: '180px',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      outline: 'none',
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box' as const,
      flexShrink: 0,
    },
    feedbackInput: {
      width: '100%',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      outline: 'none',
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box' as const,
    },
    feedbackEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      lineHeight: tokens.typography.body.lineHeight,
    },
    warningBanner: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: tokens.spacing.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      borderRadius: tokens.radius.md,
      border: `1px solid ${tokens.ui.workflowDraft}`,
      backgroundColor: `color-mix(in srgb, ${tokens.ui.workflowDraft} 12%, transparent)`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '13px',
      color: tokens.text.primary,
      lineHeight: 1.4,
      marginTop: tokens.spacing.sm,
    },
    warningIcon: {
      fontSize: '14px',
      color: tokens.ui.workflowDraft,
      marginTop: '2px',
      flexShrink: 0,
    },
  }), [isModal, titleLevel, tokens]);

  return (
    <div ref={containerRef} style={styles.container}>
      <style>{editorInnerStyles}</style>

      {/* Title with heading level selector (block mode only) */}
      {!isModal && (
        <div>
          {showBlockAuthoringControls && (
            <TitleLevelSelector
              currentLevel={titleLevel}
              onSelectLevel={(level) => onChange({ titleLevel: level as CategorizationTitleLevel })}
              tokens={tokens}
            />
          )}
          <input
            type="text"
            value={titleOverride.displayValue ?? ''}
            placeholder="Title"
            onChange={(e) => handleTitleChange(e.target.value)}
            onFocus={() => handleFieldFocus('title')}
            onBlur={handleFieldBlur}
            style={styles.titleInput}
            autoFocus={autoFocus}
          />
          {!translationMode && (
            <FieldOverrideStatus
              isDiverged={titleOverride.isDiverged}
              onRevert={handleTitleRevert}
              fieldLabel="title"
            />
          )}
        </div>
      )}

      {/* Question selection (block mode only) */}
      {showBlockAuthoringControls && (
        <QuestionSelector
          block={block}
          onChange={onChange as (updates: Record<string, unknown>) => void}
        />
      )}

      {(isModal || isManaged) && (
        <>
      {/*
        Question text.

        NA-382: block-overridable, so it is editable in BOTH the create-question
        modal (authoring the bank value) and the lesson block (overriding it) —
        hence no `isModal` gate and `managed={false}`. Every sibling field stays
        read-only and managed. It was previously read-only PLAIN TEXT in block
        mode, so rich formatting becomes visible here for the first time.
      */}
      {questionText.isEditable ? (
      <div style={isModal ? undefined : styles.sectionCard}>
        <QuestionSectionLabel
          label="Question text"
          managed={false}
          align="split"
          trailing={questionText.showStatus ? (
            <FieldOverrideStatus
              isDiverged={questionText.override.isDiverged}
              onRevert={questionText.onRevert}
              fieldLabel="question text"
            />
          ) : undefined}
        />
        <InteractionRichTextField
          content={questionText.content ?? EMPTY_RICH_TEXT_DOC}
          placeholder="Instructions for the learner..."
          isFocused={focusedField === 'description'}
          onFocus={() => handleFieldFocus('description')}
          onBlur={handleFieldBlur}
          onContentChange={questionText.onContentChange}
          autoFocus={autoFocus}
          containerRef={containerRef}
          editorClassName="tiptap categorization-editor"
          editorStyle={styles.descriptionEditor}
          toolbarPreset="full"
          toolbarStyles={richTextToolbarStyles}
        />
      </div>
      ) : descriptionText !== '' ? (
        /*
          Translation mode, question text NOT overridden: the field stays Question
          Bank-owned, so it is shown as read-only localized context rather than as
          an editable block field (`lesson-block-translation`). Editing it here
          would let a translation claim ownership, which the default-locale scope
          alone may do.
        */
        <div style={styles.sectionCard}>
          <QuestionSectionLabel label="Question text" managed={isManaged} align="split" />
          <div style={styles.readOnlyText}>{descriptionText}</div>
        </div>
      ) : null}

      {/* Categories */}
      <div style={styles.sectionCard}>
        <QuestionSectionLabel label="Categories" managed={isManaged} align="split" />
        {isModal ? (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleCategoryDragEnd}
            >
              <SortableContext
                items={block.categories.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {block.categories.map((category) => (
                  <SortableCategoryRow
                    key={category.id}
                    category={category}
                    categoryCount={block.categories.length}
                    onLabelChange={handleCategoryLabelChange}
                    onIconChange={handleCategoryIconChange}
                    onRemove={handleCategoryRemove}
                    styles={styles}
                    tokens={tokens}
                    disableOptionRemoval={disableOptionRemoval && (lockedCategoryIds ? lockedCategoryIds.has(category.id) : true)}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {block.categories.length < 3 && (
              <button
                type="button"
                style={styles.addLink}
                onClick={handleCategoryAdd}
              >
                + Add category
              </button>
            )}
          </>
        ) : (
          block.categories.map((category) => (
            <div key={category.id} style={styles.categoryRow}>
              <ReadOnlyIconBox icon={category.icon} tokens={tokens} />
              <input
                type="text"
                value={category.label}
                placeholder="Category label"
                disabled
                onChange={() => undefined}
                style={styles.categoryInput}
              />
            </div>
          ))
        )}
      </div>

      {/* Items to sort */}
      <div style={styles.sectionCard}>
        <QuestionSectionLabel label="Items to sort" managed={isManaged} align="split" />
        <div style={styles.itemsHeader}>
          <div style={{ width: '36px', flexShrink: 0 }} />
          <div style={styles.itemsHeaderLabel}>Item label</div>
          <div style={styles.itemsHeaderCategoryLabel}>Correct category</div>
          <div style={{ width: '28px', flexShrink: 0 }} />
        </div>
        {isModal ? (
          <>
            {block.items.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                itemCount={block.items.length}
                availableCategories={availableCategories}
                onLabelChange={handleItemLabelChange}
                onIconChange={handleItemIconChange}
                onCategoryChange={handleItemCategoryChange}
                onRemove={handleItemRemove}
                styles={styles}
                tokens={tokens}
                disableOptionRemoval={disableOptionRemoval && (lockedItemIds ? lockedItemIds.has(item.id) : true)}
              />
            ))}
            <button
              type="button"
              style={styles.addLink}
              onClick={handleItemAdd}
            >
              + Add item
            </button>
            {block.items.some((item) => !item.correctCategoryId) && (
              <div style={styles.warningBanner} role="alert">
                <span style={styles.warningIcon} aria-hidden="true">
                  <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: 14 }} />
                </span>
                <span>You must select a category for each item or learners will be unable to proceed</span>
              </div>
            )}
          </>
        ) : (
          block.items.map((item) => (
            <div key={item.id} style={styles.itemRow}>
              <ReadOnlyIconBox icon={item.icon} tokens={tokens} />
              <input
                type="text"
                value={item.label}
                placeholder="Item label"
                disabled
                onChange={() => undefined}
                style={styles.itemLabelInput}
              />
              <select
                value={item.correctCategoryId}
                disabled
                onChange={() => undefined}
                style={{
                  ...styles.itemCategorySelect,
                  color: item.correctCategoryId ? tokens.text.primary : tokens.text.muted,
                  cursor: 'default',
                }}
              >
                <option value="">Select category</option>
                {block.categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          ))
        )}
      </div>

      {/* Feedback — block-only; authored on the lesson block, not the create modal. */}
      {!isModal && (
      <div style={styles.sectionCard}>
        <QuestionSectionLabel label="Feedback" managed={false} />
        <div style={styles.feedbackFieldsColumn}>
          <div>
            <div style={styles.sectionLabelRow}><span style={styles.sectionLabel}>Correct heading</span></div>
            <input
              type="text"
              value={block.correctFeedbackHeading ?? 'Perfect!'}
              placeholder="Perfect!"
              onChange={(e) => onChange({ correctFeedbackHeading: e.target.value })}
              onFocus={() => handleFieldFocus('correctFeedbackHeading')}
              onBlur={handleFieldBlur}
              style={styles.feedbackInput}
            />
          </div>

          <div>
            <div style={styles.sectionLabelRow}><span style={styles.sectionLabel}>Correct feedback</span></div>
            <InteractionRichTextField
              content={block.correctFeedbackBody ?? EMPTY_RICH_TEXT_DOC}
              placeholder="Message shown when learner answers correctly..."
              isFocused={focusedField === 'correctFeedbackBody'}
              onFocus={() => handleFieldFocus('correctFeedbackBody')}
              onBlur={handleFieldBlur}
              onContentChange={(content) => onChange({ correctFeedbackBody: content })}
              autoFocus={false}
              containerRef={containerRef}
              editorClassName="tiptap categorization-editor"
              editorStyle={styles.feedbackEditor}
              toolbarPreset="full"
              toolbarStyles={richTextToolbarStyles}
            />
          </div>

          <div>
            <div style={styles.sectionLabelRow}><span style={styles.sectionLabel}>Incorrect heading</span></div>
            <input
              type="text"
              value={block.incorrectFeedbackHeading ?? 'Not quite right'}
              placeholder="Not quite right"
              onChange={(e) => onChange({ incorrectFeedbackHeading: e.target.value })}
              onFocus={() => handleFieldFocus('incorrectFeedbackHeading')}
              onBlur={handleFieldBlur}
              style={styles.feedbackInput}
            />
          </div>

          <div>
            <div style={styles.sectionLabelRow}><span style={styles.sectionLabel}>Incorrect feedback</span></div>
            <InteractionRichTextField
              content={block.incorrectFeedbackBody ?? EMPTY_RICH_TEXT_DOC}
              placeholder="Message shown when learner has items to correct..."
              isFocused={focusedField === 'incorrectFeedbackBody'}
              onFocus={() => handleFieldFocus('incorrectFeedbackBody')}
              onBlur={handleFieldBlur}
              onContentChange={(content) => onChange({ incorrectFeedbackBody: content })}
              autoFocus={false}
              containerRef={containerRef}
              editorClassName="tiptap categorization-editor"
              editorStyle={styles.feedbackEditor}
              toolbarPreset="full"
              toolbarStyles={richTextToolbarStyles}
            />
          </div>
        </div>
      </div>
      )}
        </>
      )}
    </div>
  );
}

CategorizationEditor.displayName = 'CategorizationEditor';
